import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import { INVENTORY_LOCATIONS_COLLECTION } from '@/services/inventoryMovements/constants';
import type {
  InventoryLocation,
  InventoryLocationInput,
} from '@/services/inventoryMovements/types';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';

type LocationDoc = {
  companyId: string;
  name: string;
  description: string;
  active: boolean;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapLocation(id: string, data: LocationDoc): InventoryLocation {
  return {
    id,
    companyId: data.companyId,
    name: data.name,
    description: data.description ?? '',
    active: data.active,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listInventoryLocations(
  companyId: string = appConfig.companyId,
): Promise<InventoryLocation[]> {
  const snap = await getDocs(
    query(
      collection(getFirestoreDb(), INVENTORY_LOCATIONS_COLLECTION),
      where('companyId', '==', companyId),
    ),
  );
  return snap.docs
    .map((d) => mapLocation(d.id, d.data() as LocationDoc))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function getInventoryLocation(
  id: string,
): Promise<InventoryLocation | null> {
  const snap = await getDoc(
    doc(getFirestoreDb(), INVENTORY_LOCATIONS_COLLECTION, id),
  );
  if (!snap.exists()) return null;
  return mapLocation(snap.id, snap.data() as LocationDoc);
}

export async function saveInventoryLocation(
  input: InventoryLocationInput,
  id?: string,
): Promise<InventoryLocation> {
  return withFirestoreError(async () => {
    const ref = id
      ? doc(getFirestoreDb(), INVENTORY_LOCATIONS_COLLECTION, id)
      : doc(collection(getFirestoreDb(), INVENTORY_LOCATIONS_COLLECTION));
    const existing = await getDoc(ref);
    const before = existing.exists()
      ? toAuditSnapshot(mapLocation(existing.id, existing.data() as LocationDoc))
      : undefined;

    await setDoc(
      ref,
      {
        companyId: appConfig.companyId,
        name: input.name.trim(),
        description: input.description.trim(),
        active: input.active,
        ...buildTimestampPayload(!existing.exists()),
      },
      { merge: true },
    );

    const saved = await getDoc(ref);
    if (!saved.exists()) throw new Error('No se pudo guardar la ubicación.');
    const result = mapLocation(saved.id, saved.data() as LocationDoc);
    recordEntityWrite({
      entityType: 'inventoryLocation',
      entityId: result.id,
      isCreate: !existing.exists(),
      ...(before !== undefined ? { before } : {}),
      after: toAuditSnapshot(result),
    });
    return result;
  }, 'inventoryLocation');
}
